import {useUpmPlant} from '@hconnect/upm'
import {useMemo} from 'react'

import {
  KilnShellMaterial,
  KilnShellMaterialTitle,
  KilnShellMortarIndicator,
  KilnShellTooltip
} from './components'
import {DEFAULT_SHELL_HEIGHT, TEXT_DISPLAY_THRESHOLD} from './constants'
import {getBetterContrastColor, buildShellPolygon} from './utils'

import {useKilns, useRefraSettings} from '@refra/modules/common/providers'
import {useKilnConfig} from '@refra/modules/refra-planning/hooks/queries'
import {ReportMaterialInstallation} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'
interface KilnShellDrawingProps {
  meterToPixel?: number
  kilnLength?: number
  materialInstallations: ReportMaterialInstallation[]
}

export const KilnShellDrawing = ({
  meterToPixel,
  kilnLength,
  materialInstallations
}: KilnShellDrawingProps) => {
  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})

  const {selectedKiln} = useKilns()
  const {settings} = useRefraSettings()

  const {data: kilnConfig} = useKilnConfig({
    plantUpmId: upmPlant.upmId,
    kilnUpmId: selectedKiln.upmId
  })

  const cones = useMemo(() => kilnConfig?.payload.layout.kilnShell.cones ?? [], [kilnConfig])

  const conesBiggestDiameter =
    cones.reduce((max, cone) => Math.max(max, cone.dimensions.diameter ?? 0), 0) ?? 0
  const biggestDiameter = Math.max(
    Number(kilnConfig?.payload.configuration.dimensions.diameter) ?? 0,
    conesBiggestDiameter,
    1
  )

  const heightPerMeterInPixel = DEFAULT_SHELL_HEIGHT / biggestDiameter
  const kilnHeight = DEFAULT_SHELL_HEIGHT

  const kilnShellPath = useMemo(() => {
    if (!meterToPixel || !kilnLength) return ''
    return buildShellPolygon(
      cones,
      kilnLength,
      meterToPixel,
      Number(kilnConfig?.payload.configuration.dimensions.diameter),
      heightPerMeterInPixel
    )
  }, [
    cones,
    kilnLength,
    meterToPixel,
    kilnConfig?.payload.configuration.dimensions.diameter,
    heightPerMeterInPixel
  ])

  const isKilnShellSvgLoading = !meterToPixel
  if (isKilnShellSvgLoading) return null

  return (
    <g>
      <defs>
        <clipPath id="kiln-shell-clip-path">
          <path d={kilnShellPath} />
        </clipPath>
      </defs>

      <g clipPath="url(#kiln-shell-clip-path)">
        {materialInstallations.map(
          ({
            fromMeter,
            toMeter,
            isMortarRequired,
            globalColor,
            localColor,
            refractoryName,
            feedingDate
          }) => {
            const materialWidth = (toMeter - fromMeter) * meterToPixel
            const xPosition = fromMeter * meterToPixel

            const shouldDisplayTitle = materialWidth > TEXT_DISPLAY_THRESHOLD
            const effectiveColor = settings.useLocalColors
              ? (localColor ?? globalColor)
              : globalColor

            const textColor = getBetterContrastColor(effectiveColor)

            return (
              <KilnShellTooltip
                refractoryName={refractoryName ?? ''}
                fromMeter={fromMeter}
                toMeter={toMeter}
                feedingDate={feedingDate}
                key={`kiln_material_installation_${fromMeter}-${toMeter}`}
              >
                <g>
                  <KilnShellMaterial
                    x={xPosition}
                    width={materialWidth}
                    height={kilnHeight - 4}
                    color={effectiveColor}
                  />
                  {shouldDisplayTitle && (
                    <KilnShellMaterialTitle
                      x={xPosition + materialWidth / 2}
                      refractoryName={refractoryName}
                      kilnHeight={kilnHeight}
                      materialWidth={materialWidth}
                      feedingDate={feedingDate}
                      textColor={textColor}
                    />
                  )}
                  {isMortarRequired && (
                    <KilnShellMortarIndicator
                      x={xPosition}
                      y={kilnHeight - 3}
                      materialWidth={materialWidth}
                    />
                  )}
                </g>
              </KilnShellTooltip>
            )
          }
        )}
      </g>
    </g>
  )
}
